<template>
  <transition
    appear
    name="p-drawer"
  >
    <div
      v-if="should_show_drawer"
      :class="drawer_classes"
      data-test="navbar-employee-sidebar"
      role="complementary"
    >
      <!-- Client switcher + collapse/expand toggle -->
      <div
        class="flex w-full items-center gap-2"
        :class="is_desktop_drawer_expanded ? '' : 'flex-col-reverse gap-3'"
      >
        <ButtonDropdownOptions
          class="min-w-0 flex-1"
          data_test="employee-sidebar-switch-client"
        >
          <template #custom_trigger>
            <div
              class="min-w-0 cursor-pointer rounded-md transition-colors duration-200 hover:bg-primary-100"
              :class="
                is_desktop_drawer_expanded
                  ? 'flex w-fit max-w-full items-center gap-2 px-2 py-1'
                  : 'mx-auto flex size-9 items-center justify-center'
              "
            >
              <Avatar
                class="shrink-0"
                size="32"
                format="square"
                :src="current_client.attributes.picture_logo_url_s"
                :username="current_client.attributes.name"
              />
              <span
                v-if="is_desktop_drawer_expanded"
                class="min-w-0 truncate text-sm font-semibold text-gray-800"
                :title="current_client.attributes.name"
              >
                {{ current_client.attributes.name }}
              </span>
              <FontAwesomeIcon
                v-if="is_desktop_drawer_expanded"
                icon="chevron-down"
                class="shrink-0 text-gray-400 opacity-0 transition-opacity duration-150 group-hover:opacity-100"
                size="sm"
              />
            </div>
          </template>

          <DropdownOption
            v-if="has_multiple_profiles"
            data-test="sidebar-switch-company"
            @click="openSelectTenantModal"
          >
            <FontAwesomeIcon
              :icon="['far', 'building']"
              fixed-width
              size="sm"
            />
            <span>{{ $t('navbar.topnavbar.selectTeam') }}</span>
          </DropdownOption>

          <AppLink
            hide_underline
            :to="{ name: ROUTES_EMPLOYEES.CLIENT_COMPANY }"
          >
            <DropdownOption data-test="sidebar-settings">
              <FontAwesomeIcon
                :icon="['far', 'gear']"
                fixed-width
                size="sm"
              />
              <span>{{ $t('navbar.topnavbar.settings') }}</span>
            </DropdownOption>
          </AppLink>

          <AppLink
            hide_underline
            :to="{ name: ROUTES_EMPLOYEES.SETTINGS_TEAM }"
          >
            <DropdownOption data-test="sidebar-invite-manage-members">
              <FontAwesomeIcon
                :icon="['far', 'user-group']"
                fixed-width
                size="sm"
              />
              <span>{{ $t('navbar.topnavbar.inviteManageMembers') }}</span>
            </DropdownOption>
          </AppLink>
        </ButtonDropdownOptions>
        <a
          class="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-neutral-600 transition-all duration-150 hover:bg-primary-100 hover:text-neutral-800 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
          @click="toggleDesktopDrawer"
        >
          <FontAwesomeIcon
            :icon="is_desktop_drawer_expanded ? 'arrow-left-to-line' : 'arrow-right-to-line'"
            :size="mq.isMobile ? 'lg' : 'sm'"
          />
        </a>
      </div>

      <!-- Dashboard -->
      <div class="flex w-full flex-col gap-1">
        <SidebarNavItem
          :to="{ name: ROUTES_EMPLOYEES.CLIENT_DASHBOARD }"
          :label="$t('navbar.employee.dashboard')"
          icon="house"
          :is_active="is_route_active(ROUTES_EMPLOYEES.CLIENT_DASHBOARD)"
          :is_expanded="is_desktop_drawer_expanded"
          data_test="employee-navbar-dashboard"
          :on_click="closeMobileDrawer"
        />

        <SidebarNavItem
          :to="{ name: ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE, params: { profile_type: PROFILE_TYPES.CUSTOMER } }"
          :label="$t('navbar.employee.contacts')"
          icon="users"
          :is_active="is_route_active(ROUTES_EMPLOYEES.CLIENT_CONTACTS_TYPE)"
          :is_expanded="is_desktop_drawer_expanded"
          data_test="employee-navbar-contacts"
          :on_click="closeMobileDrawer"
        />

        <SidebarNavItem
          :to="overview_to"
          :label="$t('navbar.employee.all_projects')"
          icon="layer-group"
          :is_active="is_route_active(overview_route_names)"
          :is_expanded="is_desktop_drawer_expanded"
          data_test="employee-navbar-all-projects"
          :on_click="closeMobileDrawer"
        />

        <SidebarNavItem
          v-if="current_client.attributes.ooo"
          :to="{ name: ROUTES_EMPLOYEES.CLIENT_COMPANY, hash: '#ooo' }"
          :label="$t('client.labels.warningooo')"
          icon="triangle-exclamation"
          :tooltip="current_client.attributes.ooo_note"
          tone="warning"
          :is_expanded="is_desktop_drawer_expanded"
          data_test="employee-sidebar-ooo"
        />
      </div>

      <!-- Project search + list (only when expanded) -->
      <TheSidebarProjectList
        v-if="is_desktop_drawer_expanded"
        :on_close_mobile_drawer="closeMobileDrawer"
      />
      <div
        v-else
        class="flex-1"
      />

      <!-- Bottom block: Ziggu logo -->
      <div
        v-if="is_desktop_drawer_expanded"
        class="flex w-full flex-col gap-2"
      >
        <div class="flex w-full items-center justify-center py-1">
          <img
            :src="ziggu_logo_green"
            alt="Ziggu"
            class="ziggu-logo-subtle h-4"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useMqService } from '@/plugins/mq';
import SidebarNavItem from '@/app/_employees/components/sidebars/SidebarNavItem.vue';
import TheSidebarProjectList from '@/app/_employees/components/sidebars/TheSidebarProjectList.vue';
import SelectTenantModal from '@/app/shared_components/modals/SelectTenantModal.vue';
import ButtonDropdownOptions from '@/app/shared_components/buttons/ButtonDropdownOptions.vue';
import DropdownOption from '@/app/shared_components/buttons/DropdownOption.vue';
import ziggu_logo_green from '@/assets/images/Ziggu_Logo_Green.png';
import { PROFILE_TYPES } from '@/app/data/model_constants';
import { ROUTES_EMPLOYEE_OVERVIEWS, ROUTES_EMPLOYEES } from '@/app/data/route_constants';
import { local_storage, LOCALSTORAGE_OPTIONS, localStorageKey } from '@/store/lib/local_storage';

const { proxy: app } = getCurrentInstance();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const mq = useMqService();
const store = useStore();
const route = useRoute();

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const current_profile_true = computed(() => store.getters.current_profile_true);
const current_is_impersonation = computed(() => store.getters.current_is_impersonation);

const local_storage_key_drawer = computed(() =>
  localStorageKey(current_profile.value, LOCALSTORAGE_OPTIONS.NAV_DRAWER),
);

const drawer_prefs_stored = local_storage_key_drawer.value ? local_storage.get(local_storage_key_drawer.value) : {};
const is_desktop_drawer_expanded = ref(
  drawer_prefs_stored.is_expanded === undefined ? true : drawer_prefs_stored.is_expanded,
);

watch(is_desktop_drawer_expanded, (is_expanded) => {
  if (local_storage_key_drawer.value) {
    local_storage.set(local_storage_key_drawer.value, { is_expanded });
  }
});

const should_show_drawer = computed(() => (mq.isMobile ? props.modelValue : true));

const drawer_classes = computed(() => {
  const base =
    'group bg-rail text-neutral-700 grid grid-cols-1 grid-rows-[auto_auto_1fr_auto] gap-3 z-40 h-full mb-0 shrink-0 border-r border-neutral-200';

  if (mq.isMobile) {
    return `fixed top-0 left-0 w-full p-2 ${base}`;
  }
  const width = is_desktop_drawer_expanded.value ? 'w-[260px] px-4' : 'w-[60px] px-2';
  return `lg:static top-0 left-0 ${width} py-3 ${base}`;
});

const toggleDesktopDrawer = () => {
  if (mq.isMobile) {
    emit('update:modelValue', !props.modelValue);
  } else {
    is_desktop_drawer_expanded.value = !is_desktop_drawer_expanded.value;
  }
};

const closeMobileDrawer = () => {
  if (mq.isMobile) emit('update:modelValue', false);
};

// Client switcher
const has_multiple_profiles = computed(() => {
  if (current_is_impersonation.value && current_profile_true.value.type === PROFILE_TYPES.EMPLOYEE) return false;
  return current_profile_true.value.attributes.profiles_count > 1;
});

const openSelectTenantModal = () => {
  app.$buefy.modal.open({ component: SelectTenantModal });
};

const overview_to = computed(() => ({ name: ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_ALL_PROJECTS }));
const overview_route_names = Object.values(ROUTES_EMPLOYEE_OVERVIEWS);

const is_route_active = (name_or_set) =>
  Array.isArray(name_or_set) ? name_or_set.includes(route.name) : route.name === name_or_set;

watch(
  () => route.path,
  () => {
    if (mq.isMobile) closeMobileDrawer();
  },
);
</script>

<style scoped>
.p-drawer-enter-active,
.p-drawer-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.p-drawer-enter-from {
  transform: translateX(-100%);
}

.p-drawer-leave-to {
  transform: translateX(-100%);
}

.p-drawer-enter-to,
.p-drawer-leave-from {
  transform: translateX(0);
}

@media (min-width: 1024px) {
  .lg\:static {
    transition: width 0.15s ease-out;
  }
}

.ziggu-logo-subtle {
  filter: grayscale(100%);
  opacity: 0.45;
}
</style>
